/*
 * TND spirits source bridge.
 * Owns no cart state and performs no UI or checkout orchestration. Reads both
 * carts; the only writes are line-quantity edits requested by the drawer.
 * Storefront API is pinned to 2024-10: CR-8 keeps this vanilla module stable
 * across Shopify EOL/SDK churn by using the direct GraphQL endpoint only.
 */
(function () {
  'use strict';

  const SF_API = '2024-10';
  const SPIRITS_CACHE_KEY = 'tnd_spirits_lastknown';
  const BH_KEY_RE = /^([a-f0-9]{20,40})\.bourbon-hunt-usa\.myshopify\.com\.checkoutId$/;
  const CART_ID_RE = /^gid:\/\/shopify\/Cart\/[A-Za-z0-9_-]+\?key=[A-Za-z0-9_-]+$/;
  const STOREFRONT_URL = 'https://bourbon-hunt-usa.myshopify.com/api/' + SF_API + '/graphql.json';
  const CART_BODY = [
    '{',
    '  id',
    '  checkoutUrl',
    '  totalQuantity',
    '  cost {',
    '    subtotalAmount { amount currencyCode }',
    '  }',
    '  lines(first: 50) {',
    '    pageInfo { hasNextPage }',
    '    edges {',
    '      node {',
    '        id',
    '        quantity',
    '        merchandise {',
    '          ... on ProductVariant {',
    '            title',
    '            image { url }',
    '            price { amount currencyCode }',
    '            product { title }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');
  const CART_QUERY = 'query TndSpiritsCart($id: ID!) { cart(id: $id) ' + CART_BODY + ' }';
  const LINES_UPDATE =
    'mutation TndLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) { cartLinesUpdate(cartId: $cartId, lines: $lines) { cart ' +
    CART_BODY +
    ' userErrors { field message } } }';
  const LINES_REMOVE =
    'mutation TndLinesRemove($cartId: ID!, $lineIds: [ID!]!) { cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart ' +
    CART_BODY +
    ' userErrors { field message } } }';

  var memoryLastKnown = null;

  window.TND = window.TND || {};
  window.TND.sources = window.TND.sources || {};

  function cloneValue(value) {
    if (value == null) {
      return value;
    }
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value));
  }

  function deriveSpiritsCandidates() {
    var candidates = [];
    var keys = [];
    var i;
    var key;
    var match;
    var cartId;

    try {
      for (i = 0; i < localStorage.length; i += 1) {
        key = localStorage.key(i);
        if (BH_KEY_RE.test(key)) {
          keys.push(key);
        }
      }

      keys.sort();

      for (i = 0; i < keys.length; i += 1) {
        key = keys[i];
        match = key.match(BH_KEY_RE);
        cartId = localStorage.getItem(key);
        if (cartId && CART_ID_RE.test(cartId)) {
          candidates.push({
            token: match[1],
            cartId: cartId,
            key: key
          });
        }
      }
    } catch (error) {
      return [];
    }

    return candidates;
  }

  function deriveSpirits() {
    var candidates = deriveSpiritsCandidates();

    if (!candidates.length) {
      return null;
    }

    return {
      token: candidates[0].token,
      cartId: candidates[0].cartId
    };
  }

  // H-4: destroy the SDK's cart pointer for a launched GID -- don't just flag.
  // The Buy Button SDK then mints a fresh cart for any future add, so a
  // launched cart can never swallow new items or be re-offered.
  function destroyCartKeys(gid) {
    var doomed = [];
    var removed = false;
    var i;
    var key;

    if (!gid) {
      return false;
    }

    try {
      for (i = 0; i < localStorage.length; i += 1) {
        key = localStorage.key(i);
        if (BH_KEY_RE.test(key) && localStorage.getItem(key) === gid) {
          doomed.push(key);
        }
      }
      for (i = 0; i < doomed.length; i += 1) {
        localStorage.removeItem(doomed[i]);
        removed = true;
      }
    } catch (error) {
      // Storage may be unavailable; the launched flag still blocks re-offer.
    }

    try {
      if (memoryLastKnown && memoryLastKnown.gid === gid) {
        memoryLastKnown = null;
      }
      var raw = sessionStorage.getItem(SPIRITS_CACHE_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && parsed.gid === gid) {
          sessionStorage.removeItem(SPIRITS_CACHE_KEY);
        }
      }
    } catch (error) {
      // Cache cleanup is best-effort.
    }

    return removed;
  }

  function isAgeVerified() {
    var parts = document.cookie ? document.cookie.split(';') : [];
    var i;
    var part;

    for (i = 0; i < parts.length; i += 1) {
      part = parts[i].trim();
      if (part === 'tnd_age_verified=1') {
        return true;
      }
    }

    return false;
  }

  function failure(error) {
    return {
      ok: false,
      error: error,
      count: 0,
      items: []
    };
  }

  function normalizeTndBody(body) {
    var items = Array.isArray(body.items) ? body.items : [];

    return {
      ok: true,
      count: typeof body.item_count === 'number' ? body.item_count : 0,
      items: items.map(function (item) {
        var sellingPlanName = null;

        if (item && item.selling_plan_allocation) {
          sellingPlanName = item.selling_plan_allocation.selling_plan
            ? item.selling_plan_allocation.selling_plan.name
            : null;
        }

        return {
          key: item.key,
          title: item.title,
          variantTitle: item.variant_title,
          sellingPlanName: sellingPlanName,
          qty: item.quantity,
          finalLinePriceCents: item.final_line_price,
          image: item.image,
          url: item.url
        };
      }),
      itemsSubtotalCents: body.items_subtotal_price,
      totalCents: body.total_price,
      currency: body.currency
    };
  }

  async function readTnd() {
    var response;
    var body;

    try {
      response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response || !response.ok) {
        return failure('cart-http');
      }

      body = await response.json();
      return normalizeTndBody(body);
    } catch (error) {
      return failure('cart-read');
    }
  }

  async function changeTnd(lineKey, quantity) {
    var qty = Math.max(0, Math.floor(Number(quantity) || 0));
    var response;
    var body;

    if (!lineKey) {
      return failure('cart-write');
    }

    try {
      response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: qty })
      });

      if (!response || !response.ok) {
        return failure('cart-write');
      }

      body = await response.json();
      return normalizeTndBody(body);
    } catch (error) {
      return failure('cart-write');
    }
  }

  function readSessionCache() {
    var raw;
    var parsed;

    if (memoryLastKnown) {
      return cloneValue(memoryLastKnown);
    }

    try {
      raw = sessionStorage.getItem(SPIRITS_CACHE_KEY);
      if (!raw) {
        return null;
      }
      parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        memoryLastKnown = parsed;
        return cloneValue(memoryLastKnown);
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  function writeSessionCache(value) {
    var cached = cloneValue(value);

    memoryLastKnown = cached;

    try {
      sessionStorage.setItem(SPIRITS_CACHE_KEY, JSON.stringify(cached));
    } catch (error) {
      // Private browsing can reject sessionStorage; in-memory cache still works.
    }
  }

  function cachePayload(state, items, checkoutUrl, subtotalAmount, currency, count, gid) {
    return {
      state: state,
      gid: gid || null,
      items: items,
      checkoutUrl: checkoutUrl,
      subtotalAmount: subtotalAmount,
      currency: currency,
      count: count,
      cachedAt: Date.now()
    };
  }

  function emptyResult(checkoutUrl, subtotalAmount, currency, gid) {
    var cached = cachePayload('EMPTY', [], checkoutUrl || null, subtotalAmount || null, currency || null, 0, gid || null);

    writeSessionCache(cached);
    return cloneValue(cached);
  }

  function degradedResult(source) {
    var cached = readSessionCache();
    var gid = source && source.cartId ? source.cartId : null;

    if (cached) {
      return {
        state: 'DEGRADED',
        gid: cached.gid || gid,
        items: cloneValue(cached.items) || [],
        checkoutUrl: cached.checkoutUrl || null,
        subtotalAmount: cached.subtotalAmount == null ? null : cached.subtotalAmount,
        currency: cached.currency || null,
        count: typeof cached.count === 'number' ? cached.count : 0,
        cachedAt: cached.cachedAt || null
      };
    }

    return {
      state: 'DEGRADED',
      gid: gid,
      items: [],
      checkoutUrl: null,
      subtotalAmount: null,
      currency: null,
      count: 0,
      cachedAt: null
    };
  }

  function validateCart(cart) {
    return !!(
      cart &&
      typeof cart === 'object' &&
      typeof cart.checkoutUrl === 'string' &&
      cart.checkoutUrl &&
      cart.cost &&
      cart.cost.subtotalAmount &&
      cart.cost.subtotalAmount.amount != null &&
      cart.lines &&
      Array.isArray(cart.lines.edges) &&
      cart.lines.pageInfo &&
      typeof cart.lines.pageInfo === 'object'
    );
  }

  function hasThrottleError(errors) {
    var i;
    var error;

    if (!Array.isArray(errors)) {
      return false;
    }

    for (i = 0; i < errors.length; i += 1) {
      error = errors[i];
      if (error && error.extensions && error.extensions.code === 'THROTTLED') {
        return true;
      }
    }

    return false;
  }

  function normalizeSpiritsItem(edge) {
    var node = edge && edge.node ? edge.node : {};
    var merchandise = node.merchandise || {};
    var product = merchandise.product || {};
    var price = merchandise.price || {};
    var image = merchandise.image || {};

    return {
      id: node.id,
      title: product.title,
      variantTitle: merchandise.title,
      qty: node.quantity,
      priceAmount: price.amount,
      currency: price.currencyCode,
      image: image.url || null
    };
  }

  async function storefrontFetch(source, query, variables) {
    return fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': source.token
      },
      body: JSON.stringify({
        query: query,
        variables: variables
      })
    });
  }

  function finalizeCart(cart, source) {
    var subtotal;
    var items;
    var cached;

    if (cart === null) {
      return emptyResult(null, null, null, source.cartId);
    }

    if (!validateCart(cart)) {
      return degradedResult(source);
    }

    // CR: >50 lines would be silently incomplete, so serve last-known only.
    if (cart.lines.pageInfo.hasNextPage === true) {
      return degradedResult(source);
    }

    subtotal = cart.cost.subtotalAmount;
    items = cart.lines.edges.map(normalizeSpiritsItem);

    if (items.length === 0) {
      return emptyResult(cart.checkoutUrl, subtotal.amount, subtotal.currencyCode, source.cartId);
    }

    cached = cachePayload(
      'OK',
      items,
      cart.checkoutUrl,
      subtotal.amount,
      subtotal.currencyCode,
      typeof cart.totalQuantity === 'number' ? cart.totalQuantity : items.length,
      source.cartId
    );

    writeSessionCache(cached);

    // The checkout step must still require a fresh OK read, never cached.
    return cloneValue(cached);
  }

  // A 401 means that key's token is dead (e.g. BH rotated it) -- the old key
  // lingers in localStorage forever, so retrying the same derivation can never
  // heal. Walk the remaining discovered keys instead. If EVERY known token is
  // auth-dead (deterministic 401s, not a network blip), prune the dead keys so
  // the next add mints a fresh cart instead of a forever-DEGRADED phantom.
  async function fetchWith401Failover(query, variablesFor) {
    var candidates = deriveSpiritsCandidates();
    var source = null;
    var response = null;
    var deadKeys = [];
    var i;

    if (!candidates.length) {
      return { source: null, response: null, noCart: true };
    }

    for (i = 0; i < candidates.length && i < 3; i += 1) {
      source = candidates[i];
      try {
        response = await storefrontFetch(source, query, variablesFor(source));
      } catch (error) {
        return { source: source, response: null, noCart: false };
      }
      if (response.status !== 401) {
        break;
      }
      deadKeys.push(source.key);
      response = null;
    }

    if (response === null && deadKeys.length === Math.min(candidates.length, 3)) {
      try {
        for (i = 0; i < deadKeys.length; i += 1) {
          localStorage.removeItem(deadKeys[i]);
        }
      } catch (error) {
        // Best-effort prune; the DEGRADED fallback below still applies.
      }
      // All known tokens rejected: the cart is unreachable with anything we
      // hold. Report no cart rather than serving stale items as if live.
      return { source: null, response: null, noCart: true };
    }

    return { source: source, response: response, noCart: false };
  }

  async function readSpirits() {
    var attempt;
    var body;
    var cart;

    attempt = await fetchWith401Failover(CART_QUERY, function (source) {
      return { id: source.cartId };
    });

    if (attempt.noCart) {
      return {
        state: 'NO_CART',
        gid: null,
        items: [],
        checkoutUrl: null,
        subtotalAmount: null,
        currency: null,
        count: 0,
        cachedAt: null
      };
    }

    if (!attempt.response || !attempt.response.ok) {
      return degradedResult(attempt.source);
    }

    try {
      body = await attempt.response.json();
    } catch (error) {
      return degradedResult(attempt.source);
    }

    cart = body && body.data ? body.data.cart : null;

    if (body && body.errors && (hasThrottleError(body.errors) || !cart)) {
      return degradedResult(attempt.source);
    }

    return finalizeCart(cart, attempt.source);
  }

  async function mutateSpirits(query, variablesFor, opKey) {
    var attempt;
    var body;
    var payload;

    attempt = await fetchWith401Failover(query, variablesFor);

    if (attempt.noCart) {
      return readSpirits();
    }

    if (!attempt.response || !attempt.response.ok) {
      return degradedResult(attempt.source);
    }

    try {
      body = await attempt.response.json();
    } catch (error) {
      return degradedResult(attempt.source);
    }

    payload = body && body.data ? body.data[opKey] : null;

    // userErrors (stale line id, completed cart, ...) -- resync from a read.
    if (!payload || (payload.userErrors && payload.userErrors.length)) {
      return readSpirits();
    }

    return finalizeCart(payload.cart || null, attempt.source);
  }

  async function updateSpiritsLine(lineId, quantity) {
    var qty = Math.max(0, Math.floor(Number(quantity) || 0));

    if (!lineId) {
      return readSpirits();
    }

    if (qty === 0) {
      return mutateSpirits(
        LINES_REMOVE,
        function (source) {
          return { cartId: source.cartId, lineIds: [lineId] };
        },
        'cartLinesRemove'
      );
    }

    return mutateSpirits(
      LINES_UPDATE,
      function (source) {
        return { cartId: source.cartId, lines: [{ id: lineId, quantity: qty }] };
      },
      'cartLinesUpdate'
    );
  }

  window.TND.sources.SF_API = SF_API;
  window.TND.sources.deriveSpirits = deriveSpirits;
  window.TND.sources.destroyCartKeys = destroyCartKeys;
  window.TND.sources.isAgeVerified = isAgeVerified;
  window.TND.sources.readTnd = readTnd;
  window.TND.sources.changeTnd = changeTnd;
  window.TND.sources.readSpirits = readSpirits;
  window.TND.sources.updateSpiritsLine = updateSpiritsLine;
}());
